import { css } from 'aphrodite';
import * as React from 'react';
import { Dialog, DialogBody, DialogPopup } from '../Dialog';
import { IImageCropperProps, ImageCropper } from './ImageCropper';
import { styleSheet } from './styles';

export type { ICropArea, ICropImageOptions } from './cropImage';
export { cropImage } from './cropImage';
export type { CropPresetKey, ICropPreset } from './presets';
export type { IImageCropperProps } from './ImageCropper';
export { ImageCropper } from './ImageCropper';

export interface IImageCropDialogProps extends Omit<IImageCropperProps, 'onCancel'> {
	onOpenChange: (open: boolean) => void;
	open: boolean;
	title?: string;
}

/** {@link ImageCropper} in a dialog, for a caller with nowhere to put the cropper inline. */
export const ImageCropDialog: React.FC<IImageCropDialogProps> = ({
	onCropComplete,
	onOpenChange,
	open,
	title = 'Crop image',
	...cropperProps
}) => {
	// Counts up whenever the dialog opens, closes or unmounts. An upload that settles after the user closed
	// and reopened it belongs to the old session, and closing the new one would throw away their work.
	const sessionRef = React.useRef(0);
	React.useEffect(
		() => () => {
			sessionRef.current += 1;
		},
		[open]
	);

	const handleCropComplete = async (croppedFile: File) => {
		const session = sessionRef.current;
		await onCropComplete(croppedFile);
		if (session === sessionRef.current) {
			onOpenChange(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styleSheet.popup]} showCloseButton>
				<DialogBody>
					<h3 className={css(styleSheet.title)}>{title}</h3>
					<ImageCropper {...cropperProps} onCancel={() => onOpenChange(false)} onCropComplete={handleCropComplete} />
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
};
